import React, { useState, useRef } from "react";
import useApi from "@/hooks/useApi";
import VariablePicker from "@/components/VariablePicker";

// Built-in variables always available without any custom fields configured
const BUILT_IN_VARS = [
  { name: "name",      label: "Full Name",           scope: "built-in", description: "Visitor's full name" },
  { name: "firstName", label: "First Name",           scope: "built-in", description: "First name" },
  { name: "lastName",  label: "Last Name",            scope: "built-in", description: "Last name" },
  { name: "email",     label: "Email",                scope: "built-in", description: "Email address" },
  { name: "phone",     label: "Phone",                scope: "built-in", description: "Phone number" },
  { name: "language",  label: "Language",             scope: "built-in", description: "Browser language, e.g. en, es, fr" },
  { name: "channel",   label: "Channel",              scope: "built-in", description: "Chat channel: web, sms, facebook, whatsapp" },
];

interface VarItem {
  name: string;
  label: string;
  scope: string;
  description?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * A text input that supports inline variable insertion.
 *
 * - Type @ to open a filterable variable dropdown (replaces @searchterm with <<variable_name>>)
 * - Click the << >> button to open the full VariablePicker
 * - Typing <<name>> directly always works too
 */
export default function VariableTextInput({
  value,
  onChange,
  placeholder,
  className
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [atPos, setAtPos] = useState(-1);
  const [search, setSearch] = useState("");
  const [customFields, setCustomFields] = useState<VarItem[]>([]);
  const loadedRef = useRef(false);
  const api = useApi();

  // Lazy-load custom fields the first time @ is typed
  async function ensureFieldsLoaded() {
    if (loadedRef.current) return;
    loadedRef.current = true;
    try {
      const res = await api.messaging.get("ContactFieldDefinitions/list");
      if (res.ok) {
        const data = await res.json();
        const defs = Array.isArray(data) ? data : (data?.definitions ?? []);
        setCustomFields(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          defs.map((d: any) => ({
            name:  d.name  || "",
            label: d.label || d.name || "",
            scope: d.scope || "conversation",
            description: d.description || `Custom ${d.type || "text"} field`
          }))
        );
      }
    } catch {
      // Keep built-ins only if API is unavailable
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newVal = e.currentTarget.value;
    const cursor = e.currentTarget.selectionStart ?? newVal.length;
    const beforeCursor = newVal.slice(0, cursor);
    const atIdx = beforeCursor.lastIndexOf("@");

    if (atIdx !== -1 && !beforeCursor.slice(atIdx + 1).includes(" ")) {
      // Active @ trigger: everything after @ up to cursor is the search term
      const searchTerm = beforeCursor.slice(atIdx + 1).toLowerCase();
      setAtPos(atIdx);
      setSearch(searchTerm);
      ensureFieldsLoaded();
    } else {
      setAtPos(-1);
      setSearch("");
    }
    onChange(newVal);
  }

  function insert(variable: string) {
    const cursor = inputRef.current?.selectionStart ?? value.length;
    const before = atPos !== -1 ? value.slice(0, atPos) : value;
    const after  = atPos !== -1 ? value.slice(cursor)   : "";
    onChange(before + variable + after);
    setAtPos(-1);
    setSearch("");
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  const isOpen = atPos !== -1;
  const all = [...BUILT_IN_VARS, ...customFields];
  const filtered = search
    ? all.filter(
        (v) =>
          v.name.toLowerCase().includes(search) ||
          v.label.toLowerCase().includes(search)
      )
    : all;

  return (
    <div
      className={`relative px-3 w-full h-10 bg-white rounded-lg border border-slate-300 flex gap-2 items-center text-sm ${className ?? ""}`}
    >
      <input
        ref={inputRef}
        placeholder={placeholder ?? "Value (type @ to insert a variable)"}
        value={value}
        onChange={handleChange}
        onKeyDown={(e) => {
          if (e.key === "Escape" && isOpen) {
            setAtPos(-1);
            setSearch("");
          }
        }}
        className="w-full hover:border-none focus:border-none focus:outline-none"
      />

      {/* @ dropdown */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl z-[60] max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">
              No variables match &quot;{search}&quot;
            </div>
          ) : (
            filtered.map((v) => (
              <button
                key={v.name}
                type="button"
                // onMouseDown prevents blur before the click registers
                onMouseDown={(e) => {
                  e.preventDefault();
                  insert(`<<${v.name}>>`);
                }}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 text-sm border-b border-slate-50 last:border-0"
              >
                <code className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded shrink-0">
                  {`<<${v.name}>>`}
                </code>
                <span className="text-slate-700 flex-1 truncate">{v.label}</span>
                {v.description && (
                  <span className="text-[10px] text-slate-400 shrink-0 hidden sm:block">
                    {v.description}
                  </span>
                )}
              </button>
            ))
          )}
          <div className="px-3 py-1.5 bg-slate-50 text-[10px] text-slate-400 border-t border-slate-100">
            Select to insert • Esc to cancel • Or type{" "}
            <code className="bg-slate-200 px-0.5 rounded font-mono">{`<<name>>`}</code>{" "}
            directly
          </div>
        </div>
      )}

      {/* When @ not active, show the standard picker button */}
      {!isOpen && (
        <VariablePicker onInsert={(v) => onChange(value + v)} />
      )}
    </div>
  );
}
